import { useEffect } from 'react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { type FC, useState } from 'react';
import type { LocaleLocaleAdminReadModel } from '@r/api/generated/client.schemas.ts';

type Locale = LocaleLocaleAdminReadModel;

interface LocaleData {
  content: string;
}

interface LocaleToEditFormProps {
  locale: Locale;
  onEditCustom: (localeId: number, data: LocaleData) => Promise<void>;
  onCreateCustom: (originalLocale: Locale, data: LocaleData) => Promise<void>;
  onSuccess: () => void;
}

export const LocaleToEditForm: FC<LocaleToEditFormProps> = ({
  locale,
  onEditCustom,
  onCreateCustom,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const isEditingCustom = locale.isCustom === true;
  const isCreatingCustom = locale.isCustom === false;

  const editLocaleFormSchema = z.object({
    content: z.string().min(1, {
      message: t('admin.translations.edit.form.content.error')
    })
  });

  const editLocaleForm = useForm<z.infer<typeof editLocaleFormSchema>>({
    resolver: zodResolver(editLocaleFormSchema),
    defaultValues: {
      content: locale.content || ''
    }
  });

  const handleEditLocale = async (data: z.infer<typeof editLocaleFormSchema>) => {
    if (!locale.id) return;
    setIsLoading(true);

    try {
      if (isEditingCustom) {
        await onEditCustom(locale.id, { content: data.content });
      } else if (isCreatingCustom) {
        await onCreateCustom(locale, { content: data.content });
      }
      onSuccess();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (locale) {
      editLocaleForm.reset({
        content: locale.content || ''
      });
    }
  }, [locale, editLocaleForm]);

  const getFormTitle = () => {
    if (isEditingCustom) {
      return t('admin.translations.edit.custom.title');
    }
    if (isCreatingCustom) {
      return t('admin.translations.create.custom.title');
    }
    return t('admin.translations.edit.default.title');
  };

  const getButtonText = () => {
    if (isEditingCustom) {
      return t('admin.translations.edit.custom.button');
    }
    if (isCreatingCustom) {
      return t('admin.translations.create.custom.button');
    }
    return t('admin.translations.edit.default.button');
  };

  return (
    <div>
      <h3 className="mb-4 text-lg font-medium">{getFormTitle()}</h3>

      <Form {...editLocaleForm}>
        <form onSubmit={editLocaleForm.handleSubmit(handleEditLocale)} className="space-y-4">
          <div>
            <FormLabel className="text-sm font-medium">
              {t('admin.translations.edit.form.label')}
            </FormLabel>
            <Input disabled value={locale.label || ''} className="mt-1" />
          </div>

          {isCreatingCustom && (
            <div>
              <FormLabel className="text-sm font-medium">
                {t('admin.translations.edit.form.default.content')}
              </FormLabel>
              <Input
                disabled
                value={locale.defaultContent || locale.content || ''}
                className="mt-1 bg-muted"
              />
            </div>
          )}

          {isEditingCustom && (
            <div>
              <FormLabel className="text-sm font-medium">
                {t('admin.translations.edit.form.current.content')}
              </FormLabel>
              <Input disabled value={locale.content || ''} className="mt-1 bg-muted" />
            </div>
          )}

          <FormField
            control={editLocaleForm.control}
            name="content"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>
                  {isCreatingCustom
                    ? t('admin.translations.edit.form.new.custom.content')
                    : t('admin.translations.edit.form.new.content')}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('admin.translations.edit.form.content.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button loading={isLoading} className="w-full" type="submit">
            {getButtonText()}
          </Button>
        </form>
      </Form>
    </div>
  );
};
